/**
 * The facts every workspace tree row must carry OUT OF A LISTING, in one place.
 *
 * Three mappings built these by hand — two in `WorkspaceExplorer`, one in
 * `LinkedFolderTree` — and the hand-copying is what made the same defect land in
 * three consecutive review rounds: a flag added to one mapping and not the other,
 * so the identical dead pointer was refused a share link at the top of a tree and
 * offered one inside it. Guarding that with a source-text test then oscillated
 * between crying wolf at a reflow and missing a renamed field. One function removes
 * the class instead of policing it: there is now exactly one place a flag can be
 * dropped, and it has ordinary tests.
 */
import type { FileListItem } from '@papan/files/client'

/**
 * Pointer kind, approval state and share health, exactly as the listing sent them.
 *
 * `reshareBlocked` and `shareBroken` need a cast: the client's `FileListItem` type
 * does not declare them even though the server sends them, which is filed. The
 * drive reads them the same way (`useFiles`).
 */
export function pointerFactsFromListing(f: FileListItem) {
  return {
    fileType: f.fileType,
    ...(f.chainNeedsApproval ? { chainNeedsApproval: true } : {}),
    ...(f.chainCanApprove ? { chainCanApprove: true } : {}),
    ...(f.chainBroken ? { chainBroken: true } : {}),
    ...((f as { reshareBlocked?: boolean }).reshareBlocked ? { reshareBlocked: true } : {}),
    ...((f as { shareBroken?: boolean }).shareBroken ? { shareBroken: true } : {}),
    // code-file approval (#1452): a `.py` / `.sql` whose content no person set — the row shows Approve; the content hash is
    // the version an Approve names (`useApproveCode`)
    ...(f.codeNeedsApproval ? { codeNeedsApproval: true, contentHash: f.contentHash ?? null } : {}),
    ...(f.codeCanApprove ? { codeCanApprove: true } : {}),
    ...(f.codeApprovalBelow ? { codeApprovalBelow: true } : {}),
  }
}

/**
 * A row as a context-menu handler receives it from a tree.
 *
 * ONE declaration, shared by the tree that emits it and the explorer that reads it.
 * It used to be spelled out structurally at three sites, where deleting a field was
 * invisible to the whole suite and to both typechecks — which is how round 11 found
 * a payload type that could not even hold the flag it was supposed to pass. (It also
 * omitted `chainCanApprove` at all three; that is restored here.)
 *
 * How much the single declaration actually buys, measured rather than assumed:
 * dropping `reshareBlocked`, `shareBroken` or `parentName` fails the typecheck,
 * because the explorer names those. The four pointer facts reach the gate through
 * `itemPointer: item` instead, and every one of them is OPTIONAL on
 * `PendingPointerLike`, so the assignment keeps compiling without them — property
 * access cannot catch those. `_noFactMissing` below is what does.
 */
export interface ContextMenuRow {
  id: string
  name: string
  isFolder: boolean
  parentId?: string | null
  parentName?: string | null
  fileType?: string | null
  chainNeedsApproval?: boolean
  chainCanApprove?: boolean
  chainBroken?: boolean
  reshareBlocked?: boolean
  shareBroken?: boolean
  codeNeedsApproval?: boolean
  codeCanApprove?: boolean
  codeApprovalBelow?: boolean
  contentHash?: string | null
}

/** Everything `pointerFactsFromListing` puts on a row. */
type Facts = ReturnType<typeof pointerFactsFromListing>

/**
 * Compile-time: every fact a mapping produces is DECLARED on `ContextMenuRow`.
 *
 * Without this, dropping `chainBroken` (or any of the four facts the gate reads
 * through `itemPointer` rather than by name) typechecks clean and the whole suite
 * passes — they are optional on `PendingPointerLike`, so nothing downstream
 * complains. The error lands here instead, naming the missing key.
 */
type _MissingFromRow = Exclude<keyof Facts, keyof ContextMenuRow>
const _noFactMissing: _MissingFromRow extends never ? true : _MissingFromRow = true
void _noFactMissing
